import apiClient from './apiClient';

/**
 * Event service for handling scheduling/calendar API calls
 */
const eventService = {
  /**
   * Get all events with optional filters
   * @param {Object} params - Query parameters (filters)
   * @param {string} params.startDate - ISO date string for range start
   * @param {string} params.endDate - ISO date string for range end
   * @param {string} params.category - 'external' | 'internal' | 'personal'
   * @param {string} params.eventType - 'appointment' | 'meeting' | 'task' | 'reminder' | 'time_block'
   * @param {string} params.status - 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
   * @param {string} params.locationId - UUID location filter
   * @param {string} params.assigneeId - UUID employee filter
   * @param {number} params.customerId - Integer customer filter
   * @returns {Promise} API response with events
   */
  getEvents: async (params = {}) => {
    const response = await apiClient.get('/events', { params });
    return response.data;
  },

  /**
   * Get event statistics for dashboard
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - ISO date string for range start
   * @param {string} params.endDate - ISO date string for range end
   * @returns {Promise} API response with event statistics
   */
  getStatistics: async (params = {}) => {
    const response = await apiClient.get('/events/statistics', { params });
    return response.data;
  },

  /**
   * Get single event by ID
   * @param {string} eventId - Event ID (UUID)
   * @returns {Promise} API response with event data
   */
  getEvent: async (eventId) => {
    const response = await apiClient.get(`/events/${eventId}`);
    return response.data;
  },

  /**
   * Create new event
   * @param {Object} eventData - Event information
   * @param {string} eventData.title - Event title (required)
   * @param {string} eventData.startTime - ISO datetime string (required)
   * @param {string} eventData.endTime - ISO datetime string
   * @param {string} eventData.description - Event description
   * @param {string} eventData.eventType - 'appointment' | 'meeting' | 'task' | 'reminder' | 'time_block'
   * @param {string} eventData.category - 'external' | 'internal' | 'personal'
   * @param {boolean} eventData.allDay - Is this an all-day event
   * @param {string} eventData.locationType - 'in_person' | 'virtual' | 'phone'
   * @param {string} eventData.locationAddress - Physical address
   * @param {string} eventData.locationUrl - Virtual meeting URL
   * @param {number} eventData.customerId - Associated customer ID
   * @param {number} eventData.invoiceId - Associated invoice ID
   * @param {string} eventData.locationId - Associated location UUID
   * @param {string} eventData.color - Custom color for event
   * @param {string} eventData.visibility - 'default' | 'public' | 'private'
   * @returns {Promise} API response with created event
   */
  createEvent: async (eventData) => {
    const response = await apiClient.post('/events', eventData);
    return response.data;
  },

  /**
   * Update existing event
   * @param {string} eventId - Event ID (UUID)
   * @param {Object} updateData - Updated event information
   * @returns {Promise} API response with updated event
   */
  updateEvent: async (eventId, updateData) => {
    const response = await apiClient.put(`/events/${eventId}`, updateData);
    return response.data;
  },

  /**
   * Change event status
   * @param {string} eventId - Event ID (UUID)
   * @param {string} status - New status ('scheduled' | 'in_progress' | 'completed' | 'cancelled')
   * @param {string|null} reason - Reason for status change
   * @returns {Promise} API response with updated event
   */
  changeStatus: async (eventId, status, reason = null) => {
    const response = await apiClient.put(`/events/${eventId}/status`, {
      status,
      reason
    });
    return response.data;
  },

  /**
   * Delete event
   * @param {string} eventId - Event ID (UUID)
   * @returns {Promise} API response confirming deletion
   */
  deleteEvent: async (eventId) => {
    const response = await apiClient.delete(`/events/${eventId}`);
    return response.data;
  },

  // --- Assignee Management ---

  /**
   * Get event assignees
   * @param {string} eventId - Event ID (UUID)
   * @returns {Promise} API response with assignees array
   */
  getAssignees: async (eventId) => {
    const response = await apiClient.get(`/events/${eventId}/assignees`);
    return response.data;
  },

  /**
   * Add assignee to event
   * @param {string} eventId - Event ID (UUID)
   * @param {string} employeeId - Employee ID (UUID)
   * @param {Object} options - Assignment options
   * @param {boolean} options.isOrganizer - Is this employee the organizer
   * @param {boolean} options.requiresResponse - Does this require RSVP
   * @returns {Promise} API response with assignee data
   */
  addAssignee: async (eventId, employeeId, options = {}) => {
    const response = await apiClient.post(`/events/${eventId}/assignees`, {
      employeeId,
      ...options
    });
    return response.data;
  },

  /**
   * Remove assignee from event
   * @param {string} eventId - Event ID (UUID)
   * @param {string} employeeId - Employee ID (UUID)
   * @returns {Promise} API response confirming removal
   */
  removeAssignee: async (eventId, employeeId) => {
    const response = await apiClient.delete(`/events/${eventId}/assignees/${employeeId}`);
    return response.data;
  },

  /**
   * Update assignee response (RSVP)
   * @param {string} eventId - Event ID (UUID)
   * @param {string} employeeId - Employee ID (UUID)
   * @param {string} responseStatus - 'tentative' | 'accepted' | 'declined'
   * @returns {Promise} API response with updated assignee
   */
  updateAssigneeResponse: async (eventId, employeeId, responseStatus) => {
    const response = await apiClient.put(`/events/${eventId}/assignees/${employeeId}/response`, {
      responseStatus
    });
    return response.data;
  },

  // --- Locations ---

  /**
   * Get company locations
   * @returns {Promise} API response with locations array
   */
  getLocations: async () => {
    const response = await apiClient.get('/events/locations');
    return response.data;
  },

  /**
   * Create new location
   * @param {Object} locationData - Location information
   * @param {string} locationData.name - Location name (required)
   * @param {string} locationData.addressLine1 - Street address
   * @param {string} locationData.addressLine2 - Suite/unit
   * @param {string} locationData.city - City
   * @param {string} locationData.state - State/province
   * @param {string} locationData.postalCode - Postal/zip code
   * @param {string} locationData.country - Country
   * @param {string} locationData.timezone - Timezone (e.g., 'America/New_York')
   * @param {boolean} locationData.isPrimary - Is this the primary location
   * @returns {Promise} API response with created location
   */
  createLocation: async (locationData) => {
    const response = await apiClient.post('/events/locations', locationData);
    return response.data;
  },

  // --- Form Options ---

  /**
   * Get event type options for forms
   * @returns {Promise} API response with event type options
   */
  getEventTypes: async () => {
    const response = await apiClient.get('/events/options/event-types');
    return response.data;
  },

  /**
   * Get category options for forms
   * @returns {Promise} API response with category options
   */
  getCategories: async () => {
    const response = await apiClient.get('/events/options/categories');
    return response.data;
  },

  /**
   * Get status options for forms
   * @returns {Promise} API response with status options
   */
  getStatuses: async () => {
    const response = await apiClient.get('/events/options/statuses');
    return response.data;
  }
};

export default eventService;
